import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight, CheckCircle2, Target } from "lucide-react";
import client from "../../lib/api";

/**
 * WeeklyDigest.
 *
 * Klair's short in-app note for the week: where every goal stands and
 * the one move that matters for each. Shows once per ISO week until
 * dismissed. Renders nothing when there is nothing to say.
 */
export function WeeklyDigest({ base }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    client.get("/engagement/digest")
      .then((r) => setData(r?.data || null))
      .catch(() => setData(null));
  }, []);

  if (hidden || !data || !data.has_any || data.seen) return null;

  const dismiss = () => {
    setHidden(true);
    client.post("/engagement/digest/seen").catch(() => {});
  };

  return (
    <div
      className="k-card mb-5 relative overflow-hidden k-fade-in"
      style={{ background: "hsl(var(--klareet-teal) / 0.06)", border: "1px solid hsl(var(--klareet-teal) / 0.25)" }}
      data-testid="weekly-digest"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[hsl(var(--klareet-canvas-2))] transition-colors"
        aria-label="dismiss"
        data-testid="digest-dismiss"
      >
        <X size={15} style={{ color: "hsl(var(--klareet-muted))" }} />
      </button>

      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-teal))", color: "#fff" }}>
          <Sparkles size={17} />
        </span>
        <div>
          <div className="k-label !mb-0">Klair's weekly note</div>
          <div className="k-quiet !text-xs">{data.line}</div>
        </div>
      </div>

      <div className="space-y-2.5">
        {(data.goals || []).map((g) => (
          <Link
            key={g.goal_id}
            to={`${base}/roadmap?goal=${g.goal_id}`}
            className="block rounded-xl p-3.5 transition-all hover:-translate-y-0.5"
            style={{ background: "hsl(var(--klareet-canvas))", border: "1px solid hsl(var(--klareet-line-soft))" }}
            data-testid={`digest-goal-${g.goal_id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {g.complete ? <CheckCircle2 size={14} style={{ color: "hsl(var(--klareet-teal))" }} /> : <Target size={14} style={{ color: "hsl(var(--klareet-teal))" }} />}
                <span className="font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }}>{g.goal_title}</span>
              </div>
              <span className="klareet-mono !text-[10px] shrink-0" style={{ color: "hsl(var(--klareet-muted))" }}>{g.progress_label}</span>
            </div>
            <div className="k-quiet !text-xs mt-1.5 flex items-start gap-1.5">
              <ArrowRight size={12} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-teal))" }} />
              <span><span className="font-medium" style={{ color: "hsl(var(--klareet-ink))" }}>next:</span> {g.one_move}</span>
            </div>
          </Link>
        ))}
      </div>

      <div className="k-quiet !text-xs mt-3" data-testid="digest-closing">{data.closing}</div>
    </div>
  );
}

export default WeeklyDigest;
